<template>
    <v-form v-model="isFormValid" @submit.prevent="save">
        <v-card class="pa-8">
            <v-card-title>Edit a pupil</v-card-title>
            <v-card-text>
                <label for="name">Name</label>
                <v-text-field id="name" v-model="form.name" :rules="[rules.required]" variant="outlined" />
                <label for="username">Username</label>
                <v-text-field
                    id="username"
                    v-model="form.username"
                    :rules="[rules.uniqueUsername, rules.required]"
                    variant="outlined"
                />
                <label for="password">Password</label> Leave blank to keep existing password
                <v-text-field
                    id="password"
                    v-model="form.password"
                    :rules="[rules.atLeast8Chars, rules.noSpacesAtEnds]"
                    variant="outlined"
                    :append-inner-icon="passwordVisible ? 'mdi-eye-off' : 'mdi-eye'"
                    :type="passwordVisible ? 'text' : 'password'"
                    :autocomplete="false"
                    @click:append-inner="passwordVisible = !passwordVisible"
                />
                <label for="classes">Class(es)</label>
                <v-select
                    id="classes"
                    v-model="form.classes"
                    variant="outlined"
                    chips
                    multiple
                    closable-chips
                    :items="classes"
                    item-title="name"
                    item-value="uid"
                ></v-select>
            </v-card-text>

            <v-card-actions>
                <v-btn :disabled="!isFormValid" :loading="isSaving" variant="flat" type="submit" color="brand"
                    >Save</v-btn
                >
                <v-btn :disable="isSaving" variant="outlined" to="/admin/view-pupils" color="brand">Cancel</v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useAdminStore } from '~/stores/admin';
import useSnackbar from '~/composables/useSnackbar';
import { PupilUpdate } from '@ilteducation/giglets-types';

definePage({ meta: { area: 'pupils' } });

const route = useRoute('/admin/pupil/[pupilId]');
const router = useRouter();
const store = useAdminStore();
store.fetchClasses();
store.fetchPupils();
const { pupils, classes } = storeToRefs(store);

const pupilId = ref(parseInt(route.params.pupilId + '') || 0);
const form = ref<PupilUpdate>({
    name: '',
    username: '',
    password: '',
    classes: [],
});
const isFormValid = ref(false);
const isSaving = ref(false);
const passwordVisible = ref(false);

watchEffect(() => {
    const pupil = pupils.value.find((t: any) => t.id === pupilId.value);
    if (!pupil) {
        return;
    }
    form.value = {
        name: pupil.name,
        username: pupil.username,
        password: '',
        classes: pupil.classes.map((c) => c.uid),
    };
});

const rules = {
    required: (value: string) => !!value || 'Required field.',
    uniqueUsername: (value: string) => {
        const pupil = pupils.value.find((t) => t.username === value && t.id !== pupilId.value);
        return !pupil || 'Username already exists.';
    },
    atLeast8Chars: (value: string) => {
        return !value || value.length >= 8 || 'Password must be at least 8 characters.';
    },
    noSpacesAtEnds: (value: string) => {
        return !value || value.trim().length === value.length || 'Password cannot start or end with a space.';
    },
};

const save = async () => {
    if (!isFormValid.value) {
        return;
    }

    isSaving.value = true;
    try {
        await store.updatePupil(pupilId.value, form.value);
        useSnackbar().snackbarSuccess('Pupil updated successfully.');
        await router.push('/admin/view-pupils');
    } finally {
        isSaving.value = false;
    }
};
</script>
